import * as React from 'react'

import { useTable } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { CheckboxField } from '@/modules/shared/components'
import { Loader } from '@/ui'

export type AdminColumn<T> = {
  key: string
  header: string
  /** Zawartość komórki; brak = `String(row[key])`. */
  render?: (row: T) => React.ReactNode
  width?: number
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
}

type Props<T> = {
  rows: T[]
  columns: Array<AdminColumn<T>>
  rowId: (row: T) => string | number
  /** Bez tego nie ma kolumny z zaznaczaniem. */
  selectable?: boolean
  /** Wiersze, których nie można zaznaczyć (np. pozycje systemowe). */
  isSelectable?: (row: T) => boolean
  onRowClick?: (row: T) => void
  onRowDoubleClick?: (row: T) => void
  isLoading?: boolean
  orderBy?: string
  descending?: boolean
  onSort?: (key: string) => void
  idPrefix: string
  /** Dopina się pod tabelą, wewnątrz kontenera - np. stronicowanie. */
  footer?: React.ReactNode
  /**
   * Wiersze podrzędne. Gdy zwróci niepustą tablicę, wiersz dostaje strzałkę
   * rozwijania i renderuje dzieci tymi samymi kolumnami (firmy serwisowe).
   */
  subRows?: (row: T) => T[]
}

/**
 * Tabela zakładek panelu admina.
 *
 * Wszystkie zakładki miały ten sam kształt: nagłówek z sortowaniem, kolumna zaznaczania
 * i wiersze z akcjami po prawej - w react-table v6 każda opisywała to od nowa. Zaznaczenie
 * idzie przez `TableContext`, ten sam co w `/et`.
 */
export function AdminTable<T>({
  rows,
  columns,
  rowId,
  selectable = true,
  isSelectable,
  onRowClick,
  onRowDoubleClick,
  isLoading,
  orderBy,
  descending,
  onSort,
  idPrefix,
  footer,
  subRows,
}: Props<T>) {
  const { t } = useLocalize()
  const { selectedGroup, toggleId, toggleAll, setAllIds } = useTable()
  const [expanded, setExpanded] = React.useState<Array<string | number>>([])

  const selectableRows = rows.filter(
    (row) => !isSelectable || isSelectable(row),
  )
  const allChecked =
    selectableRows.length > 0 &&
    selectedGroup?.ids.length === selectableRows.length

  // `toggleAll` zaznacza to, co jest w `allIds` kontekstu - bez tej rejestracji
  // nagłówkowy checkbox nic nie robił. Zależność to sklejone id, bo `rows` to nowa
  // tablica przy każdym renderze (`query.data ?? []`) i pętliłoby się w nieskończoność.
  const selectableIds = selectableRows.map((row) => rowId(row))
  const selectableIdsKey = selectableIds.join(',')
  React.useEffect(() => {
    if (selectable) {
      setAllIds(selectableIds.map((id) => ({ id })))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableIdsKey, selectable])

  const align = (column: AdminColumn<T>) =>
    column.align === 'center'
      ? 'tw-text-center'
      : column.align === 'right'
        ? 'tw-text-right'
        : 'tw-text-left'

  const renderRow = (row: T, id: string | number, isChild: boolean) => {
    const checked = selectedGroup?.ids.includes(id) ?? false
    const canSelect = !isSelectable || isSelectable(row)
    const children = !isChild && subRows ? subRows(row) : []
    const isExpanded = expanded.includes(id)

    return (
      <tr
        key={id}
        onClick={() => onRowClick?.(row)}
        onDoubleClick={() => onRowDoubleClick?.(row)}
        className={`tw-border-b tw-border-base-300 hover:tw-bg-primary/10 ${
          checked ? 'tw-bg-primary/10 tw-font-medium' : ''
        } ${isChild ? 'tw-bg-base-200/40' : ''} ${
          onRowClick || onRowDoubleClick ? 'tw-cursor-pointer' : ''
        }`}
      >
        {subRows && (
          <td className="tw-w-8 tw-px-1 tw-text-center tw-text-content-base">
            {children.length > 0 && (
              <button
                type="button"
                className="tw-px-1 tw-opacity-70 hover:tw-opacity-100"
                onClick={(event) => {
                  event.stopPropagation()
                  setExpanded((prev) =>
                    prev.includes(id)
                      ? prev.filter((item) => item !== id)
                      : [...prev, id],
                  )
                }}
              >
                {isExpanded ? '\u25be' : '\u25b8'}
              </button>
            )}
          </td>
        )}
        {selectable && (
          <td className="tw-w-12 tw-px-4 tw-py-3">
            <CheckboxField
              id={`${idPrefix}-${id}`}
              checked={checked}
              disabled={!canSelect}
              onChange={() => toggleId(id, true)}
            />
          </td>
        )}
        {columns.map((column, index) => (
          <td
            key={column.key}
            className={`tw-px-4 tw-py-3 tw-text-sm ${align(column)} ${
              isChild && index === 0 ? 'tw-pl-10' : ''
            }`}
          >
            {column.render
              ? column.render(row)
              : String((row as any)[column.key] ?? '')}
          </td>
        ))}
      </tr>
    )
  }

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
        <table className="tw-w-full tw-border-collapse">
          <thead className="tw-sticky tw-top-0 tw-z-10 tw-bg-content-primary">
            <tr>
              {subRows && (
                <th className="tw-w-8 tw-border-b tw-border-base-300" />
              )}
              {selectable && (
                <th className="tw-w-12 tw-border-b tw-border-base-300 tw-px-4 tw-py-3">
                  <CheckboxField
                    id={`${idPrefix}-all`}
                    checked={allChecked}
                    onChange={(event) => toggleAll(event.target.checked, true)}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={column.width ? { width: column.width } : undefined}
                  onClick={
                    column.sortable && onSort
                      ? () => onSort(column.key)
                      : undefined
                  }
                  className={`tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80 ${align(column)} ${
                    column.sortable && onSort
                      ? 'tw-cursor-pointer hover:tw-bg-base-200'
                      : ''
                  }`}
                >
                  {column.header}
                  {column.sortable && orderBy === column.key && (
                    <span className="tw-ml-1 tw-text-primary">
                      {descending ? '↓' : '↑'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const id = rowId(row)
              const children = subRows ? subRows(row) : []
              const isExpanded = expanded.includes(id)
              return (
                <React.Fragment key={id}>
                  {renderRow(row, id, false)}
                  {isExpanded &&
                    children.map((child) =>
                      renderRow(child, rowId(child), true),
                    )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>

        {isLoading && (
          <div className="tw-flex tw-h-64 tw-items-center tw-justify-center">
            <Loader size="lg" />
          </div>
        )}
        {!isLoading && rows.length === 0 && (
          <div className="tw-text-content-muted tw-flex tw-h-64 tw-items-center tw-justify-center">
            {t('common.no-data')}
          </div>
        )}
      </div>
      {footer}
    </div>
  )
}

/** Kolorowa pigułka - status/typ w kilku zakładkach. */
export const Pill: React.FC<{ color: string; children: React.ReactNode }> = ({
  color,
  children,
}) => (
  <span
    className="tw-inline-block tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-text-white"
    style={{ backgroundColor: color }}
  >
    {children}
  </span>
)

/** Pasek nad tabelą z wyszukiwarką i miejscem na dodatkowe filtry. */
export const AdminToolbar: React.FC<{
  search: string
  onSearch: (value: string) => void
  children?: React.ReactNode
}> = ({ search, onSearch, children }) => {
  const { t } = useLocalize()
  return (
    <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-6 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
      <input
        className="tw-w-[280px] tw-rounded tw-border tw-border-base-300 tw-px-3 tw-py-1.5 tw-text-sm tw-outline-none focus:tw-border-primary"
        placeholder={t('common.search')}
        value={search}
        onChange={(event) => onSearch(event.target.value)}
      />
      {children}
    </div>
  )
}
